import { FC, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Home } from 'lucide-react';

import { BookingProvider, useBooking } from '../../contexts/BookingContext';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';

import { ProgressIndicator } from './ProgressIndicator';
import { NavigationControls } from './NavigationControls';
import {
  Step1ServiceSelection,
  Step2TherapistSelection,
  Step3DateTimeSelection,
  Step4CustomerDetails,
  Step5ReviewConfirm,
} from './steps';

import { BookingStep } from '../../types/booking.types';

/**
 * Inner component that uses the booking context
 */
const BookingPageContent: FC = () => {
  const navigate = useNavigate();
  const {
    currentStep,
    formData,
    isSubmitting,
    bookingResult,
    nextStep,
    previousStep,
    goToStep,
    submitBooking,
    resetBooking,
  } = useBooking();

  // Determine which steps are completed based on form data
  const completedSteps = useMemo((): BookingStep[] => {
    const completed: BookingStep[] = [];

    if (formData.serviceId) completed.push(1);
    if (formData.serviceId && formData.therapistId !== undefined) completed.push(2);
    if (formData.date && formData.startTime && formData.endTime) completed.push(3);
    if (formData.paymentMethod) completed.push(4);

    return completed;
  }, [formData]);

  // Check if current step is valid to proceed
  const canProceed = useMemo((): boolean => {
    switch (currentStep) {
      case 1:
        return !!formData.serviceId;
      case 2:
        // therapistId can be null (no preference) so we just need to have made a selection
        return formData.therapistId !== undefined;
      case 3:
        return !!(formData.date && formData.startTime && formData.endTime);
      case 4:
        return !!formData.paymentMethod;
      case 5:
        return true;
      default:
        return false;
    }
  }, [currentStep, formData]);

  // Handle next button click
  const handleNext = useCallback(async () => {
    if (currentStep === 4) {
      // Submit the form in Step4 to validate and update formData
      const form = document.getElementById('customer-details-form') as HTMLFormElement;
      if (form) {
        form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }
      return;
    }

    if (currentStep === 5) {
      try {
        await submitBooking();
      } catch {
        // Error is already handled in context
      }
      return;
    }

    nextStep();
  }, [currentStep, nextStep, submitBooking]);

  // Handle step 4 form submission
  const handleStep4Submit = useCallback(() => {
    nextStep();
  }, [nextStep]);

  // Handle step click for navigation
  const handleStepClick = useCallback(
    (step: BookingStep) => {
      // Only allow going to completed steps or current step
      if (step <= currentStep || completedSteps.includes(step)) {
        goToStep(step);
      }
    },
    [currentStep, completedSteps, goToStep]
  );

  // Handle starting a new booking
  const handleNewBooking = useCallback(() => {
    resetBooking();
  }, [resetBooking]);

  // Handle going back to home
  const handleGoHome = useCallback(() => {
    navigate('/');
  }, [navigate]);

  // Get next button label based on current step
  const getNextLabel = (): string | undefined => {
    if (currentStep === 5) {
      return 'Confirm Booking';
    }
    return undefined;
  };

  // If booking is complete, show success actions
  if (bookingResult) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
        <Helmet>
          <title>Booking Confirmed | Massatherapy</title>
        </Helmet>

        <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
          <Card padding="lg">
            <Step5ReviewConfirm />

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <Button
                variant="outline"
                onClick={handleGoHome}
                leftIcon={<Home className="h-4 w-4" />}
              >
                Return Home
              </Button>
              <Button variant="primary" onClick={handleNewBooking}>
                Book Another Appointment
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <Helmet>
        <title>Book an Appointment | Massatherapy</title>
        <meta
          name="description"
          content="Book your massage therapy appointment online. Choose from our range of services, select your preferred therapist, and pick a convenient time."
        />
      </Helmet>

      {/* Header */}
      <header className="bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700">
        <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              leftIcon={<ArrowLeft className="h-4 w-4" />}
              aria-label="Go back"
            >
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Book Your Appointment</h1>
              <p className="mt-1 text-gray-600 dark:text-slate-400">
                Follow the steps below to schedule your massage therapy session.
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Progress Indicator */}
        <ProgressIndicator
          currentStep={currentStep}
          completedSteps={completedSteps}
          onStepClick={handleStepClick}
        />

        {/* Step Content */}
        <Card padding="lg" className="mb-8">
          {currentStep === 1 && <Step1ServiceSelection />}
          {currentStep === 2 && <Step2TherapistSelection />}
          {currentStep === 3 && <Step3DateTimeSelection />}
          {currentStep === 4 && <Step4CustomerDetails onSubmit={handleStep4Submit} />}
          {currentStep === 5 && <Step5ReviewConfirm />}
        </Card>

        {/* Navigation Controls */}
        <NavigationControls
          currentStep={currentStep}
          onBack={previousStep}
          onNext={handleNext}
          isNextDisabled={!canProceed}
          isSubmitting={isSubmitting}
          nextLabel={getNextLabel()}
        />
      </main>
    </div>
  );
};

/**
 * BookingPage - Multi-step booking flow for massage therapy appointments
 */
export const BookingPage: FC = () => {
  return (
    <BookingProvider>
      <BookingPageContent />
    </BookingProvider>
  );
};

export default BookingPage;
